import React, { useCallback, useEffect, useState } from "react";
import api from "../services/api";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { Button, Field, Input, Select, Tag, Modal } from "../components/ui";

const PAPEL_LABEL = { proprietario: "Proprietário", associado: "Associado", estagiario: "Estagiário" };
const TOM_PAPEL = { proprietario: "accent", associado: "neutral", estagiario: "neutral" };

const FORM_VAZIO = {
    nome: "", email: "", senha: "", papel: "associado",
    oab: "", uf_oab: "", telefone: "", ativo: true
};

const Equipe = () => {
    const { usuario } = useAuth();
    const [usuarios, setUsuarios] = useState([]);
    const [busca, setBusca] = useState("");
    const [mostrarForm, setMostrarForm] = useState(false);
    const [editando, setEditando] = useState(null);
    const [formData, setFormData] = useState(FORM_VAZIO);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState(null);

    const carregar = useCallback(async () => {
        try {
            const response = await api.get("/usuarios");
            setUsuarios(response.data);
        } catch (err) {
            console.error("Erro ao buscar equipe:", err);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const abrirCriar = () => {
        setEditando(null);
        setFormData(FORM_VAZIO);
        setErro(null);
        setMostrarForm(true);
    };

    const abrirEditar = (u) => {
        setEditando(u);
        setFormData({
            nome: u.nome, email: u.email, senha: "", papel: u.papel,
            oab: u.OAB || "", uf_oab: u.UF_OAB || "", telefone: u.telefone || "", ativo: !!u.ativo
        });
        setErro(null);
        setMostrarForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setCarregando(true);
        setErro(null);
        try {
            if (editando) {
                await api.put(`/usuarios/${editando.id_usuario}`, { ...formData, senha: formData.senha || undefined });
            } else {
                await api.post("/usuarios", formData);
            }
            setMostrarForm(false);
            carregar();
        } catch (err) {
            setErro(err.response?.data?.error || err.message);
        } finally {
            setCarregando(false);
        }
    };

    const excluir = async (u) => {
        if (!window.confirm(`Excluir ${u.nome}?`)) return;
        try {
            await api.delete(`/usuarios/${u.id_usuario}`);
            carregar();
        } catch (err) {
            alert(err.response?.data?.error || "Não foi possível excluir o usuário.");
        }
    };

    const usuariosFiltrados = usuarios.filter((u) =>
        u.nome.toLowerCase().includes(busca.toLowerCase()) ||
        u.email.toLowerCase().includes(busca.toLowerCase())
    );

    return (
        <div>
            <Header />

            <main className="page">
                <div className="toolbar">
                    <h2 style={{ margin: 0 }}>Equipe</h2>
                    <div className="toolbar-spacer">
                        <Input placeholder="Buscar" value={busca} onChange={(e) => setBusca(e.target.value)} />
                        <Button onClick={abrirCriar}>Novo perfil</Button>
                    </div>
                </div>

                <div className="scroll-x">
                    <table className="table">
                        <thead>
                            <tr><th>Nome</th><th>E-mail</th><th>Papel</th><th>OAB</th><th>Situação</th><th>Ações</th></tr>
                        </thead>
                        <tbody>
                            {usuariosFiltrados.map((u) => (
                                <tr key={u.id_usuario}>
                                    <td>{u.nome}{u.id_usuario === usuario.id_usuario && <span className="text-muted"> (você)</span>}</td>
                                    <td>{u.email}</td>
                                    <td><Tag tom={TOM_PAPEL[u.papel]}>{PAPEL_LABEL[u.papel]}</Tag></td>
                                    <td className="num">{u.OAB ? `${u.OAB}${u.UF_OAB ? "/" + u.UF_OAB : ""}` : "—"}</td>
                                    <td><Tag tom={u.ativo ? "success" : "danger"}>{u.ativo ? "ativo" : "desativado"}</Tag></td>
                                    <td style={{ display: "flex", gap: 4 }}>
                                        <Button variante="ghost" onClick={() => abrirEditar(u)}>Editar</Button>
                                        {u.id_usuario !== usuario.id_usuario && (
                                            <Button variante="danger" onClick={() => excluir(u)}>Excluir</Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {mostrarForm && (
                    <Modal titulo={editando ? "Editar perfil" : "Novo perfil"} onFechar={() => setMostrarForm(false)}>
                        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            <div className="grid-form">
                                <Field label="Nome"><Input name="nome" required value={formData.nome} onChange={handleChange} /></Field>
                                <Field label="E-mail"><Input type="email" name="email" required value={formData.email} onChange={handleChange} /></Field>
                                <Field label={editando ? "Nova senha (opcional)" : "Senha"}>
                                    <Input type="password" name="senha" minLength={6} required={!editando} autoComplete="new-password" value={formData.senha} onChange={handleChange} />
                                </Field>
                                <Field label="Papel">
                                    <Select name="papel" required value={formData.papel} onChange={handleChange}>
                                        <option value="proprietario">Proprietário</option>
                                        <option value="associado">Associado</option>
                                        <option value="estagiario">Estagiário</option>
                                    </Select>
                                </Field>
                                <Field label="OAB (6 dígitos)"><Input name="oab" maxLength={6} value={formData.oab} onChange={handleChange} /></Field>
                                <Field label="UF da OAB"><Input name="uf_oab" maxLength={2} value={formData.uf_oab} onChange={handleChange} /></Field>
                                <Field label="Telefone"><Input type="tel" name="telefone" maxLength={11} value={formData.telefone} onChange={handleChange} /></Field>
                                {editando && (
                                    <Field label="Situação">
                                        <Select value={formData.ativo ? "1" : "0"} onChange={(e) => setFormData((p) => ({ ...p, ativo: e.target.value === "1" }))}>
                                            <option value="1">Ativo</option>
                                            <option value="0">Desativado</option>
                                        </Select>
                                    </Field>
                                )}
                            </div>
                            <div className="modal-acoes">
                                {erro && <p className="erro">{erro}</p>}
                                <Button variante="secondary" onClick={() => setMostrarForm(false)}>Voltar</Button>
                                <Button type="submit" disabled={carregando}>{carregando ? "Salvando..." : "Salvar"}</Button>
                            </div>
                        </form>
                    </Modal>
                )}
            </main>
        </div>
    );
};

export default Equipe;
